import React, { useEffect, useRef, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import MultiSelectInput from '@/se/components/inputs/MultiSelectInput';
import config from '@/graph/config';
import { ConfigType } from '@/types/HospitalConfig';

const staffQuery = gql`
  query PreOpSmsEnrollmentStaff {
    staff {
      staffId
      name
      phoneNumber
    }
  }
`;

type Staff = { staffId: number; name: string; phoneNumber: string | null };
type Enrollment = { id: number; values: string | { recipients: number[] } };
const variables = { type: ConfigType.PreOpBoardStaffSms };

function PreOpStaffSmsEnrollment({ location }: RouteComponentProps) {
  const { data, loading, error } = useQuery<{ configs: Enrollment[] }>(config.list, { variables });
  const { data: staffData, loading: staffLoading, error: staffError } = useQuery<{ staff: Staff[] }>(staffQuery);
  const [create, { loading: creating }] = useMutation(config.create);
  const [update, { loading: updating }] = useMutation(config.update);
  const [selected, setSelected] = useState<number[]>([]);
  const [notice, setNotice] = useState<{ success: boolean; message: string } | null>(null);
  const section = useRef<HTMLFormElement>(null);
  const existing = data?.configs.slice().sort((a, b) => a.id - b.id)[0];
  const saving = creating || updating;

  useEffect(() => {
    try {
      const values = typeof existing?.values === 'string' ? JSON.parse(existing.values) : existing?.values;
      setSelected(Array.isArray(values?.recipients) ? values.recipients : []);
    } catch {
      setSelected([]);
    }
  }, [existing?.id, existing?.values]);

  useEffect(() => {
    if (!loading && location.hash === '#s-ready-enrollment') section.current?.scrollIntoView({ block: 'center' });
  }, [loading, location.hash]);

  const staff = staffData?.staff ?? [];
  const options = staff
    .filter(person => person.phoneNumber?.trim() || selected.includes(person.staffId))
    .map(person => ({
      value: person.staffId,
      label: `${person.name}${person.phoneNumber?.trim() ? '' : ' — No phone number'}`,
      isDisabled: !person.phoneNumber?.trim(),
    }));
  const selectedOptions = selected.map(
    id =>
      options.find(option => option.value === id) ?? {
        value: id,
        label: 'Unavailable staff member',
        isDisabled: true,
      }
  );

  const save = async () => {
    setNotice(null);
    const values = JSON.stringify({ recipients: selected });
    const refetch = { refetchQueries: [{ query: config.list, variables }], awaitRefetchQueries: true };
    try {
      if (existing) {
        const response = await update({ variables: { id: existing.id, values }, ...refetch });
        if (!response.data?.updateConfig) throw new Error('Enrollment configuration is no longer available.');
      } else {
        await create({ variables: { ...variables, values }, ...refetch });
      }
      setNotice({ success: true, message: 'S-Ready enrollment saved.' });
    } catch (_error) {
      setNotice({ success: false, message: 'Enrollment could not be saved. Please try again.' });
    }
  };

  return (
    <form
      ref={section}
      id="s-ready-enrollment"
      onSubmit={event => {
        event.preventDefault();
        if (!saving && !loading && !staffLoading && !error && !staffError) void save();
      }}
    >
      <Box mt={3}>
        <Typography variant="subtitle2" id="s-ready-enrollment-label">
          S-Ready enrollment
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Enrolled staff appear first in the PreOp Board SMS dialog. Messages are sent only after confirmation.
        </Typography>
        {error || staffError ? (
          <Alert severity="error">Could not load S-Ready enrollment. Reload to try again.</Alert>
        ) : (
          <Box my={2}>
            <MultiSelectInput
              name="preOpSmsEnrollment"
              aria-labelledby="s-ready-enrollment-label"
              options={options}
              value={selectedOptions}
              onChange={items => {
                setSelected((items ?? []).map(item => item.value));
                setNotice(null);
              }}
              loading={loading || staffLoading}
              isDisabled={saving || loading || staffLoading}
              isClearable
              isSearchable
              placeholder="Search Staff to enroll…"
            />
            <Typography variant="caption" color="textSecondary">
              Only Staff with a phone number can be enrolled.
            </Typography>
          </Box>
        )}
        <Button
          type="submit"
          variant="outlined"
          color="primary"
          disabled={saving || loading || staffLoading || Boolean(error || staffError)}
        >
          {saving ? <CircularProgress size={18} /> : 'Save enrollment'}
        </Button>
        {notice && (
          <Box mt={1}>
            <Alert severity={notice.success ? 'success' : 'error'}>{notice.message}</Alert>
          </Box>
        )}
      </Box>
    </form>
  );
}

export default withRouter(PreOpStaffSmsEnrollment);
